import { useState } from "react";
import { Button } from "flowbite-react";
import { HiPencil, HiTrash } from "react-icons/hi";
import { City } from "@/sharedTypes";
import DataTable from "@/components/Table";
import { useUpdateCityMutation, useDeleteCityMutation } from "@/api/locationApi/city";
import { toast } from "@/lib/toast";
import { getErrorMessage } from "@/utils/requestUtils";
import CityFormDialog from "./CityFormDialog";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isCompanyAdminUser } from "@/constants/agent";
import { useTranslation } from "react-i18next";

interface CityListTableProps {
  data: City[];
  pagination: any;
  onPageChange: (page: number) => void;
}

export default function CityListTable({ data, pagination, onPageChange }: CityListTableProps) {
  const { t } = useTranslation(["temporalActivity", "common"]);
  const user = useCurrentUser();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState<City | undefined>();

  const [updateCity] = useUpdateCityMutation();
  const [deleteCity] = useDeleteCityMutation();

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  } | null>(null);

  const handleEdit = (city: City) => {
    setSelectedCity(city);
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (data: City) => {
    if (!selectedCity) return;
    try {
      await updateCity({ ...data, id: selectedCity.id } as City).unwrap();
      toast.success(t("temporalActivity:city.updatedSuccess"));
      setIsEditModalOpen(false);
      setSelectedCity(undefined);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleDelete = (city: City) => {
    setConfirmDialog({
      open: true,
      title: t("temporalActivity:city.confirmDeleteTitle"),
      description: t("temporalActivity:city.confirmDeleteBody"),
      onConfirm: async () => {
        try {
          await deleteCity(city.id).unwrap();
          toast.success(t("temporalActivity:city.deletedSuccess"));
        } catch (err) {
          toast.error(getErrorMessage(err));
        }
        setConfirmDialog(null);
      },
    });
  };

  const columns = [
    { key: "id", label: t("temporalActivity:city.ref") },
    { key: "city_name", label: t("temporalActivity:city.name") },
    ...(isCompanyAdminUser(user)
      ? [
          {
            key: "actions",
            label: t("temporalActivity:actions"),
            render: (row: City) => (
              <div className="flex gap-2">
                <Button size="xs" color="light" onClick={() => handleEdit(row)}>
                  <HiPencil className="h-4 w-4 text-purple-600" />
                </Button>
                <Button size="xs" color="light" onClick={() => handleDelete(row)}>
                  <HiTrash className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <DataTable columns={columns} data={data} pagination={pagination} onPageChange={onPageChange} />

      <CityFormDialog
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditSubmit}
        defaultValues={selectedCity}
        mode="edit"
      />

      {confirmDialog && (
        <ConfirmationDialog
          title={confirmDialog.title}
          body={confirmDialog.description}
          onCancel={() => setConfirmDialog(null)}
          onConfirm={confirmDialog.onConfirm}
        />
      )}
    </>
  );
}
